import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';

// Warehouse → Mark stacks: the phone page for walking a pallet row and saying which box
// stacks stand on each pallet (S, M, SL, LL, ML). Fernando, 2026-09-28: "I want to now go to
// the warehouse and mark what is what … on the phone". One pallet at a time from the bottom
// wall up — the same order he counted them in; one tap marks the whole pallet and moves on;
// "Mixed" opens the per-section chips for a combo. Saves through POST /warehouse/floor/stacks
// (the floor plan shows the result under Colour: box stacks). Claude, 2026-09-28.

interface Code { code: string; face: string; pieces: number; box?: string | null; top?: number | null }
interface Unit { id: string; kind: string; row: string; y: number; codes: Code[]; pieces: number; top_brand: string | null; note: string | null }

const BOXES = ['S', 'M', 'SL', 'LL', 'ML'];
const BOX_COLOR: Record<string, string> = { S: '#3b82f6', M: '#a855f7', SL: '#f97316', LL: '#ef4444', ML: '#14b8a6' };

function range(codes: Code[]) {
  if (!codes.length) return '';
  const ns = codes.map(c => displayLocation(c.code));
  return ns.length > 1 ? `${ns[0]} – ${ns[ns.length - 1].split(' ')[1] ?? ns[ns.length - 1]}` : ns[0];
}

export default function MarkStacks() {
  const navigate = useNavigate();
  const [units, setUnits] = useState<Unit[] | null>(null);
  const [err, setErr] = useState('');
  const [row, setRow] = useState<string>(() => { try { return localStorage.getItem('wms_stacks_row') || ''; } catch { return ''; } });
  const [i, setI] = useState(0);
  const [mixed, setMixed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState<{ unit: string; before: Record<string, string | null> } | null>(null);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/warehouse/floor`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
      setUnits((d.units || []).filter((u: Unit) => u.kind === 'pallet'));
    })();
  }, []);

  const rows = useMemo(() => Array.from(new Set((units || []).map(u => u.row))).sort(), [units]);
  const pallets = useMemo(() => (units || []).filter(u => u.row === row).sort((a, b) => b.y - a.y), [units, row]);
  const marked = (u: Unit) => u.codes.length > 0 && u.codes.every(c => c.box);
  const done = pallets.filter(marked).length;

  // start on the first pallet of the row that is not marked yet
  useEffect(() => {
    if (!pallets.length) return;
    const k = pallets.findIndex(u => !marked(u));
    setI(k < 0 ? 0 : k); setMixed(false);
  }, [row, units === null]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(codes: string[], box: string | null, unit: Unit, advance: boolean) {
    setBusy(true); setErr('');
    const before: Record<string, string | null> = {};
    unit.codes.forEach(c => { before[c.code] = c.box || null; });
    const r = await authFetch(`${API}/warehouse/floor/stacks`, { method: 'POST', body: JSON.stringify({ codes, box_type: box }) });
    setBusy(false);
    if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.detail || 'Not saved — check the connection'); return; }
    setUnits(us => (us || []).map(u => u.id !== unit.id ? u
      : { ...u, codes: u.codes.map(c => codes.includes(c.code) ? { ...c, box } : c) }));
    setUndo({ unit: unit.id, before });
    if (advance && i < pallets.length - 1) { setI(i + 1); setMixed(false); }
  }

  async function doUndo() {
    if (!undo || !units) return;
    const unit = units.find(u => u.id === undo.unit);
    if (!unit) return;
    // put every section back to what it was, one call per previous value
    const groups: Record<string, string[]> = {};
    Object.entries(undo.before).forEach(([code, b]) => { (groups[b ?? ''] = groups[b ?? ''] || []).push(code); });
    setBusy(true);
    for (const [b, codes] of Object.entries(groups)) {
      await authFetch(`${API}/warehouse/floor/stacks`, { method: 'POST', body: JSON.stringify({ codes, box_type: b || null }) });
    }
    setBusy(false);
    setUnits(us => (us || []).map(u => u.id !== unit.id ? u
      : { ...u, codes: u.codes.map(c => ({ ...c, box: undo.before[c.code] ?? null })) }));
    const k = pallets.findIndex(u => u.id === unit.id);
    if (k >= 0) setI(k);
    setUndo(null);
  }

  if (err && !units) return <div className="p-4 text-red-400">{err}</div>;
  if (!units) return <div className="p-4 text-gray-500">Loading…</div>;

  if (!row || !rows.includes(row)) return (
    <div className="p-4 max-w-lg mx-auto">
      <button onClick={() => navigate('/settings/warehouse/tools')} className="text-gray-400 hover:text-white text-sm">← Settings · Warehouse</button>
      <h1 className="text-xl font-semibold text-white mb-1">Mark stacks</h1>
      <p className="text-sm text-gray-400 mb-4">Pick a pallet row. You go pallet by pallet from the bottom wall up and tap the box type on it.</p>
      <div className="grid grid-cols-2 gap-3">
        {rows.map(r => {
          const ps = units.filter(u => u.row === r); const m = ps.filter(marked).length;
          return (
            <button key={r} onClick={() => { setRow(r); try { localStorage.setItem('wms_stacks_row', r); } catch { /* */ } }}
              className={`rounded-xl border px-4 py-4 text-left ${m === ps.length ? 'border-emerald-700 bg-emerald-900/20' : 'border-gray-700 bg-gray-800/50'}`}>
              <div className="text-2xl font-bold text-white">{r}</div>
              <div className="text-xs text-gray-400">{m} / {ps.length} marked</div>
            </button>
          );
        })}
      </div>
      <button onClick={() => navigate('/warehouse/floor')} className="mt-5 text-sm text-gray-400 underline">Open the floor plan</button>
    </div>
  );

  const u = pallets[i];
  const [a, b] = row.split('|');                 // "J|K": K is on the left (faces K-L), J on the right
  const faces = [b, a].map(L => ({ L, codes: u.codes.filter(c => c.face === L).sort((x, y) => (x.top ?? 0) - (y.top ?? 0)) }));
  const allSame = u.codes.length && u.codes.every(c => c.box && c.box === u.codes[0].box) ? u.codes[0].box : null;

  return (
    <div className="p-3 max-w-lg mx-auto pb-24">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setRow('')} className="text-sm text-gray-400">← Rows</button>
        <div className="text-sm text-gray-300"><b className="text-white">{row}</b> · {done} / {pallets.length} marked</div>
      </div>
      <div className="h-1.5 bg-gray-800 rounded mb-3"><div className="h-1.5 bg-emerald-500 rounded" style={{ width: `${(100 * done) / Math.max(1, pallets.length)}%` }} /></div>

      <div className={`rounded-2xl border-2 p-4 ${marked(u) ? 'border-emerald-600 bg-emerald-900/10' : 'border-gray-700 bg-gray-800/40'}`}>
        <div className="flex justify-between items-baseline">
          <div className="text-gray-400 text-sm">Pallet {i + 1} of {pallets.length} from the bottom wall</div>
          <div className="text-gray-500 text-xs">{u.id}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {faces.map(fc => (
            <div key={fc.L}>
              <div className="text-xs text-gray-500">{fc.L} side</div>
              <div className="text-2xl font-bold text-white leading-tight">{range(fc.codes)}</div>
              <div className="text-xs text-gray-400">{fc.codes.reduce((n, c) => n + c.pieces, 0)} pcs</div>
            </div>
          ))}
        </div>
        {u.top_brand && <div className="text-sm text-gray-400 mt-2">mostly {u.top_brand}</div>}
        <div className="text-sm mt-2">{allSame ? <span className="text-emerald-300">All {allSame}</span>
          : u.codes.some(c => c.box) ? <span className="text-amber-300">Mixed: {u.codes.map(c => c.box || '?').join(' ')}</span>
          : <span className="text-gray-500">Not marked yet</span>}</div>
      </div>

      {!mixed ? (
        <div className="grid grid-cols-3 keep-cols gap-2 mt-4">
          {BOXES.map(bx => (
            <button key={bx} disabled={busy} onClick={() => save(u.codes.map(c => c.code), bx, u, true)}
              className="py-5 rounded-xl text-2xl font-bold text-white disabled:opacity-50"
              style={{ background: BOX_COLOR[bx], outline: allSame === bx ? '3px solid white' : undefined }}>{bx}</button>
          ))}
          <button onClick={() => setMixed(true)} className="py-5 rounded-xl text-lg font-semibold bg-gray-700 text-gray-100">Mixed…</button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {faces.map(fc => (
            <div key={fc.L}>
              <div className="text-xs text-gray-500 mb-1">{fc.L} side</div>
              {fc.codes.map(c => (
                <div key={c.code} className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-white font-semibold w-16">{displayLocation(c.code)}</span>
                  <span className="flex gap-1">
                    {BOXES.map(bx => (
                      <button key={bx} disabled={busy} onClick={() => save([c.code], c.box === bx ? null : bx, u, false)}
                        className="px-2.5 py-2 rounded-lg text-sm font-bold text-white"
                        style={{ background: c.box === bx ? BOX_COLOR[bx] : '#374151' }}>{bx}</button>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          ))}
          <button onClick={() => { setMixed(false); if (i < pallets.length - 1) setI(i + 1); }}
            className="w-full py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold">Done — next pallet</button>
        </div>
      )}

      {err && <div className="text-red-400 text-sm mt-3">{err}</div>}

      <div className="fixed bottom-0 left-0 right-0 bg-[#1a1d27] border-t border-gray-800 p-3 flex gap-2 max-w-lg mx-auto">
        <button disabled={i === 0} onClick={() => { setI(i - 1); setMixed(false); }} className="flex-1 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">← Previous</button>
        <button disabled={!undo || busy} onClick={doUndo} className="px-4 py-3 rounded-lg bg-gray-800 text-gray-200 disabled:opacity-30">Undo</button>
        <button disabled={i >= pallets.length - 1} onClick={() => { setI(i + 1); setMixed(false); }} className="flex-1 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">Next →</button>
      </div>
    </div>
  );
}
